/**
 * SearchableSelect - Campo de escolha com busca (Popover + Command)
 *
 * Mesmo visual do PersonSelector: clicar, digitar, escolher. Usar em toda lista
 * pesquisável que não seja de Pessoas (ex.: Modelo de rastreador, Plano de dados).
 * O filtro é local sobre as opções recebidas (listas já paginadas pelo servidor).
 *
 * RELEASE: 2026-10-08 v1.0.0 - Versão inicial (padrão único de seletores do Estoque).
 * RELEASE: 2026-10-09 v1.1.0 - Opção pode informar `color`: exibe bolinha colorida antes do
 *            rótulo (na lista e no valor escolhido). Sem cor, o visual não muda.
 * RELEASE: 2026-10-17 v1.2.0 - Opção pode informar `group`: lista em blocos com título; busca
 *            também pelo nome do grupo.
 */
import { useMemo, useState } from 'react';
import { Check, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export interface SearchableOption {
  value: string;
  label: string;
  /** Texto secundário exibido em cinza abaixo do rótulo. */
  hint?: string | null;
  /** Cor (hex) opcional: exibe uma bolinha antes do rótulo. */
  color?: string | null;
  /** Título do bloco (ex.: categoria). Opções consecutivas do mesmo grupo ficam juntas. */
  group?: string | null;
}

/** Agrupa mantendo a ordem recebida. */
function agrupar(options: SearchableOption[]): [string | null, SearchableOption[]][] {
  const out: [string | null, SearchableOption[]][] = [];
  for (const o of options) {
    const g = o.group ?? null;
    const last = out[out.length - 1];
    if (last && last[0] === g) last[1].push(o); else out.push([g, [o]]);
  }
  return out;
}

interface Props {
  value: string | null;
  onChange: (value: string | null) => void;
  options: SearchableOption[];
  placeholder?: string;
  /** Rótulo da opção vazia; omitir para não permitir "nenhum". */
  noneLabel?: string;
  /** Mantém o rótulo visível quando o item escolhido não está na lista carregada. */
  selectedLabel?: string | null;
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
  /** Variante compacta: lista independente do botão e nomes completos nas opções. */
  quickFilter?: boolean;
}

export function SearchableSelect({ value, onChange, options, placeholder = 'Selecionar...', noneLabel, selectedLabel, disabled, isLoading, className, quickFilter = false }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Filtro local por rótulo e texto secundário (sem acento/caixa)
  const filtered = useMemo(() => {
    const norm = (t: string) => t.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const q = norm(search.trim());
    if (!q) return options;
    return options.filter((o) => norm(`${o.label} ${o.hint ?? ''} ${o.group ?? ''}`).includes(q));
  }, [options, search]);

  const current = options.find((o) => o.value === value);
  const visible = current?.label ?? (value ? selectedLabel : null) ?? (noneLabel && !value ? noneLabel : placeholder);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" disabled={disabled} className={cn('w-full justify-between font-normal', className)}>
          <span className="flex min-w-0 items-center gap-2">
            {current?.color && <ColorDot color={current.color} />}
            <span className="truncate">{visible}</span>
          </span>
          {quickFilter ? <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" /> : <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn('p-0', quickFilter ? 'w-80 max-w-[calc(100vw-2rem)]' : 'w-[var(--radix-popover-trigger-width)]')} align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Buscar..." value={search} onValueChange={(v) => setSearch(v.slice(0, 60))} />
          <CommandList>
            <CommandEmpty>{isLoading ? 'Buscando...' : 'Nenhum resultado.'}</CommandEmpty>
            {noneLabel && !search && (
              <CommandGroup>
                <CommandItem value="__none__" onSelect={() => { onChange(null); setOpen(false); }}>
                  <span className={cn('min-w-0 flex-1', quickFilter ? 'whitespace-normal break-words' : 'truncate')}>{noneLabel}</span>
                  <Check className={cn('ml-2 h-4 w-4', !value ? 'opacity-100' : 'opacity-0')} />
                </CommandItem>
              </CommandGroup>
            )}
            {/* v1.2.0: opções com `group` viram blocos com título; sem `group`, um bloco único */}
            {agrupar(filtered).map(([titulo, itens], i) => (
              <CommandGroup key={titulo ?? `__g${i}`} heading={titulo ?? undefined}>
                {itens.map((o) => (
                  <CommandItem key={o.value} value={o.value} onSelect={() => { onChange(o.value); setOpen(false); }}>
                    {o.color && <ColorDot color={o.color} className="mr-2" />}
                    <div className="min-w-0 flex-1">
                      <p className={quickFilter ? 'whitespace-normal break-words' : 'truncate'}>{o.label}</p>
                      {o.hint && <p className={cn('text-xs text-muted-foreground', quickFilter ? 'whitespace-normal break-words' : 'truncate')}>{o.hint}</p>}
                    </div>
                    <Check className={cn('ml-2 h-4 w-4 shrink-0', value === o.value ? 'opacity-100' : 'opacity-0')} />
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Bolinha colorida usada para identificar a opção (cor vinda do cadastro). */
function ColorDot({ color, className }: { color: string; className?: string }) {
  return <span aria-hidden="true" className={cn('h-2.5 w-2.5 shrink-0 rounded-full', className)} style={{ backgroundColor: color }} />;
}
